import { Component, OnInit, ViewChildren, ElementRef, QueryList, Output, EventEmitter, ChangeDetectionStrategy } from "@angular/core";
import { FileUploader } from "ng2-file-upload";
import { UserService, WindowService, SupportService, HelperService } from "@mypxplat/xplat/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslateService } from "@ngx-translate/core";
import { NgxSpinnerService } from "ngx-spinner";
import { AppService } from "@mypxplat/xplat/web/core";
import { TicketAddCommentBaseComponent } from "@mypxplat/xplat/features";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-ticket-add-comment",
  templateUrl: "ticket-add-comment.component.html",
  standalone: false,
})
export class TicketAddCommentComponent extends TicketAddCommentBaseComponent implements OnInit {
  @Output() commentAdded: EventEmitter<any> = new EventEmitter();
  public uploader: FileUploader;
  public uploadingComplete: boolean = false;
  public uploadingFile: any;
  public fileResponse: any;
  public isUploading: boolean = false;
  public uploadProgress: number = 0;

  public attachments: Array<any> = [];

  private _disallowedFiletypes = {
    exe: true,
    sh: true,
    dmg: true,
    js: true,
    ts: true,
    sql: true,
  };

  constructor(
    userService: UserService,
    public activeModal: NgbActiveModal,
    public translate: TranslateService,
    private _spin: NgxSpinnerService,
    public appService: AppService,
    private _win: WindowService,
    public supportService: SupportService,
    public helperService: HelperService
  ) {
    super(userService, supportService);
  }

  ngOnInit() {
    this.setupUploader();
  }

  setupUploader() {
    // FileUploader is used only for the file-picker / drop-zone UI. The actual
    // upload goes through UserService.uploadZendeskAttachment (S3 staging + Zendesk complete).
    this.uploader = new FileUploader({
      isHTML5: true,
      method: "POST",
      queueLimit: 1,
      autoUpload: false,
      url: "unused://paeapi-zendesk-passthrough",
      disableMultipart: true,
    });

    this.uploader.onWhenAddingFileFailed = (e) => {
      this.translate.get("general.save").subscribe((string) => alert(string));

      return false;
    };

    this.uploader.onAfterAddingFile = (f) => {
      this.uploadingFile = f;
      f.withCredentials = false;
      const extension = f.file.name.split(".").pop().toLowerCase();
      // Zendesk ticket attachments cap at 50 MB: https://developer.zendesk.com/documentation/ticketing/managing-tickets/adding-ticket-attachments-with-the-api/
      if (f.file.size > 50 * 1024 * 1024) {
        alert("Attachments cannot exceed 50 MB.");
      } else if (this._disallowedFiletypes[extension]) {
        alert("This file type is not allowed.");
      } else {
        this._sendZendeskUpload(f);
        return;
      }
      this.uploader.clearQueue();
      this._win.setTimeout(() => {
        this.setupUploader();
      }, 100);
    };
  }

  private _sendZendeskUpload(item: any) {
    const file: File = item._file;
    this.isUploading = true;
    this.uploadProgress = 0;
    this.uploadingComplete = false;
    this.userService.uploadZendeskAttachment(file, file.name).subscribe({
      next: (event) => {
        this.uploadProgress = event.progress;
        if (event.done) {
          this.uploadingComplete = true;
          this.isUploading = false;
          this.fileResponse = event.response;
          if (this.fileResponse && this.fileResponse.attachment) {
            this.attachments.push(this.fileResponse.attachment);
          }
          this.uploader.clearQueue();
          this._win.setTimeout(() => {
            this.setupUploader();
          }, 100);
        }
      },
      error: (err) => {
        this.isUploading = false;
        this.fileResponse = null;
        alert(err?.error?.error || err?.message || "There was an error uploading your attachment. Please try again.");
        this.uploader.clearQueue();
        this._win.setTimeout(() => {
          this.setupUploader();
        }, 100);
      },
    });
  }

  submit() {
    let args: any = {
      comment: this.comment,
    };
    if (this.ticket.status == "closed") {
      args.via_followup_source_id = this.ticket.id;
      args.requester = {
        name: this.user.firstName + " " + this.user.lastName,
        email: this.user.email,
      };
    }

    if (this.attachments && this.attachments.length) {
      let tokens = [];
      this.attachments.forEach((att) => {
        if (att && att.upload && att.upload.token) tokens.push(att.upload.token);
      });
      args.uploads = tokens;
    }
    if (this.solved) args.solved = true;
    this.loading = true;
    this.supportService.addComment(args, this.ticket.id).subscribe((result) => {
      this.loading = false;
      this.commentAdded.next(result);
      this.activeModal.close();
    });
  }
}
